// FILE: components/layout/Topbar.tsx
"use client";

import { formatDiamond } from "@/utils/formatDiamond";
import NotificationDropdown from "@/components/notifications/NotificationDropdown";
import { useUnreadCount } from "@/components/notifications/useUnreadCount";
import { useState, useRef, useEffect } from "react";
import { useRouter } from "next/navigation";
import { cn } from "@/lib/utils";
import HardNavLink from "@/components/shared/HardNavLink";
import {
  PanelLeftClose, PanelLeft, Menu, MessageCircle, Search,
  User, Settings, LogOut, Route, ChevronRight,
  LogIn, Bell, Plus, HelpCircle, Shield, Gem, BarChart2, Award
} from "lucide-react";
import { signOut } from "@/actions/auth";
import type { AppUser } from "./AppShell";
import { useDiamonds } from "@/lib/DiamondsContext";
import { useCountUp } from "@/hooks/useCountUp";

type AppMode = "chat" | "explore";

interface TopbarProps {
  title: string;
  mode: AppMode;
  onModeChange: (mode: AppMode) => void;
  sidebarCollapsed: boolean;
  onToggleSidebar: () => void;
  onMobileMenuOpen: () => void;
  chatSidebarOpen?: boolean;
  onToggleChatSidebar?: () => void;
  onNavigate?: (page: string) => void;
  onOpenSearch?: () => void;
  onOpenAuth?: () => void;
  user: AppUser | null;
}

export default function Topbar({
  title, mode, onModeChange, sidebarCollapsed, onToggleSidebar,
  onMobileMenuOpen, onNavigate, onOpenSearch, onOpenAuth, user,
  chatSidebarOpen, onToggleChatSidebar,
}: TopbarProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const bellRef = useRef<HTMLDivElement>(null);
  const router = useRouter();

  // Close on outside click
  useEffect(() => {
    if (!menuOpen && !bellOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuOpen && menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
      if (bellOpen && bellRef.current && !bellRef.current.contains(e.target as Node)) setBellOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [menuOpen, bellOpen]);

  // ESC to close
  useEffect(() => {
    if (!menuOpen && !bellOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") { setMenuOpen(false); setBellOpen(false); } };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [menuOpen, bellOpen]);

  // Ctrl+K search
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if ((e.metaKey || e.ctrlKey) && e.key === "k") { e.preventDefault(); onOpenSearch?.(); } };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [onOpenSearch]);

  // ─── User display ───
  const displayName = user?.full_name || user?.username || "User";
  const displayEmail = user?.email || "";
  const avatarUrl = user?.avatar_url;
  const initials = displayName.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase() || "U";
  const { diamonds } = useDiamonds();
  const animatedDiamonds = useCountUp(diamonds, 800);
  const isAdmin = user?.role === "ADMIN";
  const { count: unreadCount, setCount: setUnreadCount } = useUnreadCount(user?.id);

  const MENU_ITEMS: Array<{ id: string; label?: string; icon?: any; destructive?: boolean }> = [
    { id: "profile",     label: "Trang cá nhân",      icon: User },
    { id: "my-learning", label: "Quản lý lộ trình",   icon: Route },
    { id: "revenue",     label: "Doanh thu",           icon: BarChart2 },
    { id: "badges",      label: "Huy hiệu",           icon: Award },
    { id: "divider" },
    { id: "settings",    label: "Cài đặt tài khoản",  icon: Settings },
    { id: "wallet",      label: "Nạp tiền - Rút tiền", icon: Gem },
    { id: "help",        label: "Trợ giúp",            icon: HelpCircle },
    { id: "divider2" },
    { id: "logout",      label: "Đăng xuất",           icon: LogOut,     destructive: true },
  ];

  // ─── Avatar button (shared between mobile & desktop) ───
  function AvatarButton({ size = 8, onClick }: { size?: number; onClick: () => void }) {
    const cls = `w-${size} h-${size}`;
    return (
      <button onClick={onClick}
        className={cn("rounded-full flex items-center justify-center cursor-pointer transition-all overflow-hidden shrink-0",
          menuOpen && "ring-2 ring-teal-500/40 ring-offset-1")}>
        {avatarUrl ? (
          <img src={avatarUrl} alt="" className={`${cls} rounded-full object-cover`} />
        ) : (
          <div className={`${cls} rounded-full bg-gradient-to-br from-teal-700 to-teal-500 flex items-center justify-center text-white text-[11px] font-extrabold`}>{initials}</div>
        )}
      </button>
    );
  }

  // ─── Dropdown menu (shared) ───
  function DropdownMenu() {
    return (
      <div className="absolute top-full right-0 mt-2 w-56 rounded-xl border border-border bg-white shadow-[0_10px_40px_rgba(0,0,0,.12)] z-[9999] overflow-hidden animate-fade-slide">
        {/* User header */}
        <div className="px-3.5 py-3 border-b border-border">
          <div className="flex items-center gap-2.5">
            {avatarUrl ? (
              <img src={avatarUrl} alt="" className="w-9 h-9 rounded-full object-cover shrink-0" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white text-[11px] font-extrabold shrink-0">{initials}</div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-bold text-teal-800 truncate">{displayName}</div>
              <div className="text-[11px] text-muted-foreground truncate">{displayEmail}</div>
            </div>
          </div>
        </div>

        {/* Admin shortcut */}
        {isAdmin && (
          <div className="px-2 pt-2 pb-1">
            <HardNavLink href="/admin" onClick={() => setMenuOpen(false)}
              className="flex items-center gap-2.5 px-3 py-2 rounded-lg bg-gradient-to-r from-[#0B1120] to-[#1e3a4a] text-white hover:from-[#1e293b] hover:to-[#1e5570] transition-all group">
              <Shield className="w-3.5 h-3.5 text-teal-400 shrink-0" />
              <span className="text-[11px] font-bold flex-1">Admin Dashboard</span>
              <ChevronRight className="w-3 h-3 text-white/30 group-hover:text-white/60" />
            </HardNavLink>
          </div>
        )}

        {/* Menu items */}
        <div className="py-1.5">
          {MENU_ITEMS.map((item) => {
            if (!("icon" in item)) return <div key={item.id} className="my-1.5 h-px bg-border mx-2" />;
            const Icon = item.icon;

            if (item.id === "logout") {
              return (
                <form key={item.id} action={signOut}>
                  <button type="submit" className="w-full flex items-center gap-2.5 px-3.5 py-2 text-[12px] font-medium text-red-500 hover:bg-red-50 transition-colors">
                    <Icon className="w-4 h-4 shrink-0 text-red-400" />
                    <span className="flex-1 text-left">{item.label}</span>
                  </button>
                </form>
              );
            }

            // Route map cho các item có URL thật
            const ITEM_ROUTES: Record<string, string> = {
              profile: user?.username ? `/${user.username}` : user?.id ? `/${user.id}` : "",
              "my-learning": "/my-paths",
              revenue: "/studio/revenue",
              badges: "/badges",
              settings: "/settings",
              wallet: "/wallet",
              help: "/help",
            };
            const href = ITEM_ROUTES[item.id];

            if (href) {
              return (
                <HardNavLink key={item.id} href={href} onClick={() => setMenuOpen(false)}
                  className="w-full flex items-center gap-2.5 px-3.5 py-2 text-[12px] font-medium text-foreground hover:bg-muted transition-colors">
                  <Icon className="w-4 h-4 shrink-0 text-muted-foreground" />
                  <span className="flex-1 text-left">{item.label}</span>
                  <ChevronRight className="w-3 h-3 text-muted-foreground/30" />
                </HardNavLink>
              );
            }

            return (
              <button key={item.id} onClick={() => { setMenuOpen(false); onNavigate?.(item.id); }}
                className="w-full flex items-center gap-2.5 px-3.5 py-2 text-[12px] font-medium text-foreground hover:bg-muted transition-colors">
                <Icon className="w-4 h-4 shrink-0 text-muted-foreground" />
                <span className="flex-1 text-left">{item.label}</span>
                <ChevronRight className="w-3 h-3 text-muted-foreground/30" />
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <>
      {/* ══ Mobile topbar ══ */}
      <div className="md:hidden flex items-center h-[3.25rem] bg-teal-800 px-3 gap-2 flex-shrink-0 border-b border-white/10 overflow-visible">
        {mode === "chat" ? (
  <button onClick={onToggleChatSidebar} className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center text-white hover:bg-white/15 transition-colors shrink-0">
    {chatSidebarOpen ? <PanelLeftClose className="w-[18px] h-[18px]" /> : <PanelLeft className="w-[18px] h-[18px]" />}
  </button>
) : (
  <button onClick={onMobileMenuOpen} className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center text-white hover:bg-white/15 transition-colors shrink-0">
    <Menu className="w-[18px] h-[18px]" />
  </button>
)}
        <span className="font-display font-extrabold text-white text-base shrink-0">JB <span className="text-orange-400">AI</span></span>

        {/* Mobile mode toggle — chỉ user đã login */}
        <div className="flex-1 flex justify-center">
          {user && (
            <div className="flex bg-white/10 rounded-full p-[2px]">
              <button onClick={() => onModeChange("chat")} className={cn("flex items-center gap-1 px-3 py-[4px] rounded-full text-[11px] font-semibold transition-all", mode === "chat" ? "bg-white text-teal-800 shadow-sm" : "text-white/60")}>
                <MessageCircle className="w-3 h-3" />Chat
              </button>
              <button onClick={() => onModeChange("explore")} className={cn("flex items-center gap-1 px-3 py-[4px] rounded-full text-[11px] font-semibold transition-all", mode === "explore" ? "bg-white text-teal-800 shadow-sm" : "text-white/60")}>
                <Search className="w-3 h-3" />Explore
              </button>
            </div>
          )}
        </div>

        {user ? (
          <div className="flex items-center gap-2">
            {/* Diamonds + Nạp nhanh */}
            <div className="flex items-center gap-1 bg-white/10 rounded-full pl-2.5 pr-1 py-[3px]">
              <span className="text-[10px]">💎</span>
              <span className="text-[10px] font-bold text-orange-300 tabular-nums">{animatedDiamonds.toLocaleString("vi-VN")}</span>
              <HardNavLink href="/wallet"
                className="w-5 h-5 rounded-full bg-orange-500 text-white flex items-center justify-center hover:bg-orange-400 ml-0.5"
                title="Nạp kim cương">
                <Plus className="w-2.5 h-2.5" />
              </HardNavLink>
            </div>

            {/* Bell */}
            <div ref={bellRef} className="relative">
              <button onClick={() => { setBellOpen(p => !p); setMenuOpen(false); }}
                className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center text-white/70 hover:bg-white/15 relative">
                <Bell className="w-4 h-4" />
              </button>
              {bellOpen && (
              <NotificationDropdown
              onClose={() => setBellOpen(false)}
              onNavigate={onNavigate}
              onUnreadCountChange={setUnreadCount}
              />
            )}
            </div>

            {/* Avatar + dropdown - desktop only */}
            <div ref={menuRef} className="relative hidden md:block">
              <AvatarButton size={8} onClick={() => { setMenuOpen(p => !p); setBellOpen(false); }} />
              {menuOpen && <DropdownMenu />}
            </div>
          </div>
        ) : (
          <button onClick={onOpenAuth} className="text-white/70 text-[12px] font-semibold px-3 py-1 rounded-lg bg-white/10 hover:bg-white/15">
            Đăng nhập
          </button>
        )}
      </div>

      {/* ══ Desktop topbar ══ */}
      <div className="hidden md:flex items-center h-14 bg-white border-b border-border px-6 gap-4 flex-shrink-0">
        <button onClick={onToggleSidebar} className="w-8 h-8 rounded-lg bg-transparent border border-border flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground transition-colors flex-shrink-0" title="Ẩn/Hiện sidebar">
          {sidebarCollapsed ? <PanelLeft className="w-4 h-4" /> : <PanelLeftClose className="w-4 h-4" />}
        </button>

        <h1 className="font-display font-bold text-teal-800 text-[0.9375rem] truncate">{title}</h1>

        {/* Search trigger */}
        <button onClick={onOpenSearch} className="flex items-center gap-2.5 h-9 px-3.5 rounded-xl border border-border bg-[#fafbfc] text-muted-foreground hover:border-teal-500/40 hover:bg-white transition-all cursor-pointer min-w-[180px] lg:min-w-[260px]">
          <Search className="w-3.5 h-3.5 shrink-0" />
          <span className="text-[12px] flex-1 text-left truncate">Tìm kiếm...</span>
          <kbd className="hidden lg:inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-muted text-[9px] font-mono text-muted-foreground/70 border border-border shrink-0">⌘K</kbd>
        </button>

        <div className="flex-1" />

        {/* Mode toggle — chỉ user đã login */}
        {user && (
          <div className="flex bg-muted rounded-full p-[3px]">
            <button onClick={() => onModeChange("chat")} className={cn("flex items-center gap-1.5 px-4 py-[5px] rounded-full text-xs font-semibold transition-all whitespace-nowrap", mode === "chat" ? "bg-teal-800 text-white shadow-[0_1px_6px_rgba(15,58,74,.3)]" : "bg-transparent text-muted-foreground hover:text-foreground")}>
              <MessageCircle className="w-3 h-3" />Chat
            </button>
            <button onClick={() => onModeChange("explore")} className={cn("flex items-center gap-1.5 px-4 py-[5px] rounded-full text-xs font-semibold transition-all whitespace-nowrap", mode === "explore" ? "bg-teal-800 text-white shadow-[0_1px_6px_rgba(15,58,74,.3)]" : "bg-transparent text-muted-foreground hover:text-foreground")}>
              <Search className="w-3 h-3" />Explore
            </button>
          </div>
        )}

        {user ? (
          <div className="flex items-center gap-2">
            {/* 💎 Diamonds + Add button */}
            <div className="flex items-center gap-1 bg-orange-50 border border-orange-200 rounded-full pl-3 pr-1 py-[3px]">
              <Gem className="w-3.5 h-3.5 text-orange-500" />
              <span className="text-[12px] font-bold text-orange-600 min-w-[20px] tabular-nums">{animatedDiamonds.toLocaleString("vi-VN")}</span>
              <HardNavLink href="/wallet"
                className="w-6 h-6 rounded-full bg-orange-500 text-white flex items-center justify-center hover:bg-orange-400 transition-colors ml-0.5"
                title="Nạp kim cương">
                <Plus className="w-3 h-3" />
              </HardNavLink>
            </div>

            {/* 🔔 Bell */}
            <div ref={bellRef} className="relative">
              <button onClick={() => { setBellOpen(p => !p); setMenuOpen(false); }}
                className="w-9 h-9 rounded-lg border border-border flex items-center justify-center text-muted-foreground hover:bg-muted hover:text-foreground transition-colors relative">
                <Bell className="w-4 h-4" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-[18px] h-[18px] rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center border-2 border-white">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </button>
              {bellOpen && (
                <NotificationDropdown
                  onClose={() => setBellOpen(false)}
                  onNavigate={onNavigate}
                  onUnreadCountChange={setUnreadCount}
                />
              )}
            </div>

            {/* 👤 Avatar */}
            <div ref={menuRef} className="relative">
              <button onClick={() => { setMenuOpen(p => !p); setBellOpen(false); }}
                className={cn("w-9 h-9 rounded-full flex items-center justify-center cursor-pointer transition-all overflow-hidden shrink-0",
                  menuOpen && "ring-2 ring-teal-500/40 ring-offset-2")}>
                {avatarUrl ? (
                  <img src={avatarUrl} alt="" className="w-9 h-9 rounded-full object-cover" />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-gradient-to-br from-teal-700 to-teal-500 flex items-center justify-center text-white text-[11px] font-extrabold">{initials}</div>
                )}
              </button>
              {menuOpen && <DropdownMenu />}
            </div>
          </div>
        ) : (
          <button onClick={onOpenAuth} className="inline-flex items-center gap-1.5 px-4 py-[6px] rounded-full bg-teal-800 text-white text-[12px] font-bold hover:bg-teal-700 transition-colors">
            <LogIn className="w-3.5 h-3.5" />Đăng nhập
          </button>
        )}
      </div>
    </>
  );
}